import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { SERVER_URL } from '@/lib/env'
import { trpc } from '@/lib/trpc'

interface RunbookExecutionStreamOptions {
  executionId: string | null
  /** Pause the stream (e.g. when the panel is closed). Defaults to enabled. */
  enabled?: boolean
  /**
   * Fired (debounced, coalesced with the run-query invalidation) on each live
   * event — lets a consumer refresh its own views too, e.g. the task detail
   * invalidating `task.board` / comments when a graduated run advances so the
   * requester's page updates the moment a manager approves.
   */
  onEvent?: () => void
}

export type RunbookExecutionStreamStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'closed'
  | 'error'

const INVALIDATE_DEBOUNCE_MS = 100

/**
 * Subscribe to live runbook execution progress over SSE and invalidate
 * the `runbook.getRun` tRPC query as events arrive. The server emits
 * lightweight pointer events (step-update / execution-update / complete
 * / failed) — the tRPC procedure is the source of truth for the DTO.
 *
 * Multiple events within INVALIDATE_DEBOUNCE_MS coalesce into a single
 * refetch to avoid thrashing during fast step transitions.
 */
export function useRunbookExecutionStream({
  executionId,
  enabled = true,
  onEvent,
}: RunbookExecutionStreamOptions): RunbookExecutionStreamStatus {
  const qc = useQueryClient()
  const [status, setStatus] = useState<RunbookExecutionStreamStatus>('idle')

  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Latest callback held in a ref so a changing identity never re-subscribes.
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  useEffect(() => {
    if (!executionId || !enabled) {
      setStatus('idle')
      return
    }
    setStatus('connecting')

    const queryKey = trpc.runbook.getRun.queryKey({ id: executionId })
    const listRunsKey = trpc.runbook.listRuns.queryKey()

    const scheduleInvalidate = () => {
      if (pendingTimerRef.current) return
      pendingTimerRef.current = setTimeout(() => {
        pendingTimerRef.current = null
        void qc.invalidateQueries({ queryKey })
        onEventRef.current?.()
      }, INVALIDATE_DEBOUNCE_MS)
    }
    const invalidateListRuns = () => {
      void qc.invalidateQueries({ queryKey: listRunsKey })
    }

    const es = new EventSource(
      `${SERVER_URL}/api/runbook-executions/${executionId}/events`,
      { withCredentials: true },
    )

    es.onopen = () => setStatus('open')

    const onEvent = () => scheduleInvalidate()
    es.addEventListener('step-update', onEvent)
    es.addEventListener('execution-update', onEvent)
    es.addEventListener('complete', () => {
      scheduleInvalidate()
      invalidateListRuns()
      setStatus('closed')
      es.close()
    })
    es.addEventListener('failed', () => {
      scheduleInvalidate()
      invalidateListRuns()
      setStatus('closed')
      es.close()
    })
    es.addEventListener('error', () => {
      if (es.readyState === EventSource.CLOSED) {
        setStatus('closed')
        return
      }
      // EventSource auto-reconnects; consumer can fall back to polling
      // while state !== 'open'.
      setStatus('error')
    })

    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
        pendingTimerRef.current = null
      }
      es.close()
    }
  }, [executionId, enabled, qc])

  return status
}
